import { memo, useCallback, type ReactNode, type SVGProps } from 'react'
import { Mic, MicOff } from 'lucide-react'
import { Button, Tooltip, type ButtonVariant } from '@highlight/ui'
import { GlobalEvents } from '@highlight/ipc'
import { useGlobalState } from '@highlight/ipc/react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import {
  MEETING_MIC_CAPTURE_OVERRIDE_EVENT,
  MEETING_MIC_GATE_KEY,
  NEUTRAL_MEETING_MIC_GATE,
  type MeetingMicGate,
} from '@shared/types/meeting-mic-gate'

/**
 * Google's `adaptive_audio_mic` Material Symbol, inlined as a React SVG so the
 * meeting UI doesn't need to pull in a Material Symbols webfont. `currentColor`
 * lets it theme with the surrounding text/icon color. It's unsized so `Button`
 * sizes it like the lucide glyphs beside it.
 */
function AdaptiveAudioMicIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M711-480Zm209 80H737q-3-21-9.5-41T711-480h126q-4-7-9-12t-12-9q-26-15-59.5-22t-76.5-7h-3q-20-23-43.5-40T582-599q23-5 47.5-8t50.5-3q53 0 99 11t86 32q26 14 40.5 41.5T920-463v63ZM680-640q-50 0-85-35t-35-85q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35Zm0-80q17 0 28.5-11.5T720-760q0-17-11.5-28.5T680-800q-17 0-28.5 11.5T640-760q0 17 11.5 28.5T680-720Zm0-40ZM249-480ZM40-400v-63q0-35 14.5-62.5T95-567q40-21 86-32t99-11q26 0 50.5 3t47.5 8q-28 12-51.5 29T283-530h-3q-43 0-76.5 7T144-501q-7 4-12 9t-9 12h126q-10 19-16.5 39t-9.5 41H40Zm240-240q-50 0-85-35t-35-85q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35Zm0-80q17 0 28.5-11.5T320-760q0-17-11.5-28.5T280-800q-17 0-28.5 11.5T240-760q0 17 11.5 28.5T280-720Zm0-40Zm200 480q-33 0-56.5-23.5T400-360v-120q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480v120q0 33-23.5 56.5T480-280ZM450-80v-82q-72-11-121-67t-49-131h60q0 58 41 99t99 41q58 0 99-41t41-99h60q0 75-49 131t-121 67v82h-60Z" />
    </svg>
  )
}

/** Capture (or stop capturing) the mic while muted, for the rest of this call. */
export function setMeetingMicCaptureOverride(enabled: boolean): void {
  GlobalEvents.emit(MEETING_MIC_CAPTURE_OVERRIDE_EVENT, enabled)
  AmplitudeRenderer.trackEvent('Meeting Mic Capture Override', { enabled })
}

interface MicStatusContent {
  icon: ReactNode
  tooltip: string
  /** What the next click sets the per-call override to. */
  nextOverride: boolean
  className: string
}

interface MeetingMicStatusProps {
  /** Outside a `ButtonGroup` shell, which otherwise sets it. */
  variant?: ButtonVariant
}

/**
 * Mic-state indicator for the live recording UI: says that mute stopped
 * capture, and un-mutes Highlight without un-muting the call when clicked.
 * Reads the `meeting-mic-gate` value, so it hides itself off-call.
 */
export const MeetingMicStatus = memo(function MeetingMicStatus({ variant }: MeetingMicStatusProps) {
  const [gate] = useGlobalState<MeetingMicGate>(MEETING_MIC_GATE_KEY, NEUTRAL_MEETING_MIC_GATE)

  let content: MicStatusContent | null = null
  if (gate.shouldDropMic) {
    content = {
      icon: <MicOff />,
      tooltip: gate.mergedAudio
        ? "You're muted in your meeting. This call shares audio with a nearby devices, nothing from your mic is being captured — click to record anyway."
        : "You're muted in your meeting. Click to record anyway.",
      nextOverride: true,
      className: 'text-warning-subtle hover:text-warning',
    }
  } else if (gate.selfMute === true && gate.captureWhileMuted) {
    content = {
      icon: gate.mergedAudio ? <AdaptiveAudioMicIcon /> : <Mic />,
      tooltip: 'Highlight is recording your mic even though the call shows you muted. Click to stop.',
      nextOverride: false,
      className: 'text-foreground-secondary hover:text-foreground',
    }
  }

  const nextOverride = content?.nextOverride ?? false
  const handleClick = useCallback(() => {
    setMeetingMicCaptureOverride(nextOverride)
  }, [nextOverride])

  if (!content) {
    return null
  }

  return (
    <Tooltip>
      {/* Variant and size come from the pill's `ButtonGroup` shell. The state
          colour overrides the ghost variant's neutral text. */}
      <Tooltip.Trigger
        render={
          <Button
            variant={variant}
            shape="square"
            aria-label={content.tooltip}
            onClick={handleClick}
            className={content.className}
          />
        }
      >
        {content.icon}
      </Tooltip.Trigger>
      <Tooltip.Content className="max-w-[240px] whitespace-pre-line leading-normal">{content.tooltip}</Tooltip.Content>
    </Tooltip>
  )
})
